// @ts-check
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { faFileExport, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ElementTooltip,
  formatDate,
  SpinnerLoader,
  useFeatureFlag,
} from 'core';

// Components.
import { Modal } from 'core/components/Modal';
import { campaignRefresh } from 'core/services/campaign-refresh-service';
import { useAppDispatch } from 'core/store';
import { ChannelExportHistoryModal } from './ChannelExportHistoryModal';
import { ExportTypeModal } from './ExportTypeModal';
import { NestedCheckbox } from './NestedCheckbox';
import { SalesforceCredentialsModal } from './SalesforceCredentialsModal';

const TableContainer = styled.div`
  overflow-x: unset;
`;

const TwoItemContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
`;

const StatusBadge = styled.span.attrs((props) => ({
  status: 'saved',
  className: `badge ${
    props.status === 'saved' ? 'text-bg-info' : 'text-bg-success'
  } text-dark p-1`,
}))`
  border-radius: 5px;
  width: 100%;
`;

const StyledLink = styled.a.attrs((props) => ({
  disabled: props.disabled || false,
}))`
  cursor: pointer;

  ${(props) =>
    props.disabled
      ? `
        --disabled-color: #6c757d;
        color: var(--disabled-color);
        cursor: not-allowed;
        text-decoration: none;
        &:hover {
          color: var(--disabled-color);
        }
      `
      : ''}
`;

const transform = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  return data.filters.map((category) => {
    const parent = {
      label: category.category,
      name: category.category.toLowerCase().replace(/ /g, '_'),
      checked: false,
      childrenNodes: [],
    };

    let parentCopy = { ...parent };

    parent.childrenNodes = category.fields.map((field) => ({
      label: field.field_label,
      name: field.db_filter_key,
      checked: field.db_filter_key === 'student_id',
      childrenNodes: [],
      parent: parentCopy,
    }));

    return parent;
  });
};

/**
 * @param {{
 *  loading: boolean,
 *  campaign: CampaignModel,
 *  latestStatement: Statement,
 *  exportFilters: Field[],
 *  exportFiltersLoading: boolean,
 *  exportInProgress: boolean,
 *  isPreviewDataLoading: boolean,
 *  totalRecordCount: number,
 *  onPublishCampaign: () => void,
 *  onExportList: (exportType?: string) => void,
 *  onExportAuditFile: () => void,
 *   sendMessage: (params: {
 *   handleError: boolean,
 *   message: any,
 *   onMessageCallback: (evt: any) => void
 *  }) => void,
 *  onDetailClick: () => void,
 * }} props
 */
function DetailList({
  loading,
  campaign,
  latestStatement,
  exportFilters,
  exportFiltersLoading,
  exportInProgress,
  isPreviewDataLoading,
  totalRecordCount,
  onPublishCampaign,
  onExportList,
  onExportAuditFile,
  sendMessage,
  onDetailClick,
}) {
  const status = campaign.published ? 'Published' : 'Saved';
  const isHidden = campaign.details.sources.length === 0;

  // Modal visibility state.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // Track which modal to show (export type selector, nested checkbox, or salesforce credentials).
  const [showNestedCheckbox, setShowNestedCheckbox] = useState(false);
  const [showSalesforceCredentials, setShowSalesforceCredentials] =
    useState(false);

  const initialList = transform(exportFilters);
  const hasExportFilterOptions = initialList.length > 0;

  // Feature flag: restrict Channel export history visibility to enabled users.
  const isSalesforceEnabled = useFeatureFlag(
    'ACAN_CPBD_SFMC_INTEGRATION_873354',
  );

  // Set any Export List checkboxes as checked if the field exists in the campaign.export_fields array.
  const updateCheckedStatus = (item) => {
    if (!campaign.exportFields) {
      return;
    }

    if (campaign.exportFields.includes(item.name)) {
      return { ...item, checked: true };
    }
    if (item.childrenNodes) {
      const updatedChildren = item.childrenNodes.map(updateCheckedStatus);
      return { ...item, childrenNodes: updatedChildren };
    }
    return item;
  };

  const updatedInitialList = initialList.map(updateCheckedStatus);
  const isExportListDisabled =
    exportInProgress || exportFiltersLoading || !hasExportFilterOptions;
  const showExportButtonSpinner = isExportListDisabled;

  // Function to close the modal and reset state.
  const onCancel = () => {
    setIsModalOpen(false);
    setShowNestedCheckbox(false);
    setShowSalesforceCredentials(false);
  };

  const closeHistory = () => setIsHistoryOpen(false);

  // Toggle modal visibility. When opening the modal, show NestedCheckbox first.
  const toggleModal = () => {
    const newModalState = !isModalOpen;
    setIsModalOpen(newModalState);
    // When opening the modal, show NestedCheckbox first.
    if (newModalState) {
      setShowNestedCheckbox(true);
    }
  };

  // Store selected fields from NestedCheckbox.
  const [selectedFields, setSelectedFields] = useState([]);

  // Handle field selection from NestedCheckbox - only stores the selected fields and shows ExportTypeModal.
  const handleFieldSelection = (fields, exportType) => {
    setSelectedFields(fields);

    if (exportType === 'csv') {
      // Feature flag is false: CSV export was already handled in NestedCheckbox.
      // Just trigger the actual export through the parent component.
      onExportList('csv');
      // Modal will be closed by NestedCheckbox's onCancel call.
    } else {
      // Feature flag is true: Show ExportTypeModal next.
      setShowNestedCheckbox(false);
    }
  };

  // Handle export type selection - actually performs the export based on selection.
  const handleExportTypeSelection = (exportType) => {
    if (exportType === 'csv') {
      // For CSV exports, perform export with the selected fields.
      // Fields were already saved to campaign in Step 1 (NestedCheckbox).
      onExportList('csv');
      // Close the modal after export
      onCancel();
    } else if (exportType === 'salesforce') {
      // For Salesforce exports, show the credentials form
      setShowSalesforceCredentials(true);
    }
  };

  // The Salesforce credentials are now submitted directly from the SalesforceCredentialsModal.
  // via API endpoint instead of being handled here.

  // If upload file exists and Export File Summary: Total Export File Records is not "n/a" then.
  // Records column should equal Total Export File Records.
  // If no upload file then Records column should equal the
  // preview table "Total rows in Campaign list" value.
  // Else there was likely an upload file but no matches, set Records column to zero (0).
  const totalRecords =
    campaign.fileName?.valueOf() !== undefined &&
    campaign.calculatedMatchSummary?.totalRecords > 0
      ? campaign.calculatedMatchSummary?.totalRecords
      : campaign.fileName?.valueOf() === undefined && totalRecordCount > 0
      ? totalRecordCount
      : 0;

  const dispatch = useAppDispatch();
  const { id: routeCampaignId } = /** @type {{ id?: string }} */ (useParams());

  const handleRefreshClick = () => {
    campaignRefresh({
      campaign,
      sendMessage,
      dispatch,
      getCurrentCampaignId: () => routeCampaignId,
    });
  };

  return (
    <div>
      <TableContainer className="uds-table mb-2" tabIndex={0}>
        <table data-testid="details-table">
          <thead>
            <tr>
              <th scope="col">File upload(s)</th>
              <th scope="col">Date Created</th>
              <th scope="col">Created By</th>
              <th scope="col">Last refreshed</th>
              <th scope="col">Records</th>
              <th scope="col">Status</th>
              <th scope="col">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {campaign.fileName ? (
                  <TwoItemContainer>
                    <span>{campaign.fileName}</span>
                    {/* TODO: add class `text-maroon` when download WEB API is ready */}
                    <i className="fas fa-download" />
                  </TwoItemContainer>
                ) : (
                  ' '
                )}
              </td>
              <td>{formatDate(campaign.createdAt)}</td>
              <td>{campaign.createdByName}</td>
              <td>
                {latestStatement?.started_at
                  ? formatDate(latestStatement.started_at)
                  : '\u2014'}
              </td>
              <td>
                {!isPreviewDataLoading ? (
                  totalRecords
                ) : (
                  <SpinnerLoader
                    spinnerType="circle"
                    width="20px"
                    height="20px"
                  />
                )}
              </td>
              <td>
                <StatusBadge status={status.toLowerCase()}>
                  {status}
                </StatusBadge>
              </td>
              <td>
                <ElementTooltip
                  tooltipId="open-detail-tooltip"
                  tooltip="Allows authorized users the ability to edit all campaign fields."
                >
                  {/* Using StyledLink instead of Unity React native "Button"
                      component to preserve text link appearance (no button
                      styling) in table cell. */}
                  <StyledLink
                    disabled={loading || campaign.published}
                    aria-describedby="open-detail-tooltip"
                    data-ga-event="link"
                    data-ga-action="click"
                    data-ga-name="onclick"
                    data-ga-type="internal link"
                    data-ga-region="main content"
                    data-ga-section="Campaign Details"
                    data-ga-component=""
                    data-ga-text="Edit all campaign fields"
                    onClick={onDetailClick}
                  >
                    <i className="fas fa-edit me-2" />
                    Edit Campaign
                  </StyledLink>
                </ElementTooltip>
              </td>
            </tr>
          </tbody>
        </table>
      </TableContainer>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <ElementTooltip
            tooltipId="export-list-tooltip"
            tooltip="Export your student list as a CSV file or to an FTP site."
          >
            {/* GTM tracking via data-ga-* attributes.
                Handled by centralized listener in @asu/component-header-footer.
                Note: Using native button due to dynamic icon requirements
                (spinner/export toggle). */}
            <button
              disabled={isExportListDisabled}
              className="btn btn-maroon"
              type="button"
              aria-describedby="export-list-tooltip"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="button"
              data-ga-region="main content"
              data-ga-section="Campaign Details"
              data-ga-component=""
              data-ga-text="Open the Export List modal on the campaign details page."
              onClick={toggleModal}
            >
              <FontAwesomeIcon
                className="me-1"
                icon={showExportButtonSpinner ? faSpinner : faFileExport}
                spin={showExportButtonSpinner}
                aria-hidden="true"
              />
              Export List
            </button>
          </ElementTooltip>
          {isSalesforceEnabled &&
            (campaign?.channel_export_history?.length || 0) > 0 && (
              <StyledLink onClick={() => setIsHistoryOpen(true)}>
                Channel export history
              </StyledLink>
            )}
        </div>
        <div className="d-flex gap-2">
          <ElementTooltip
            tooltipId="refresh-campaign-tooltip"
            tooltip="Refreshes the data for your campaign."
          >
            <button
              className="btn btn-maroon"
              type="button"
              aria-describedby="refresh-campaign-tooltip"
              disabled={isPreviewDataLoading}
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Campaign Details"
              data-ga-component=""
              data-ga-text="Refresh campaign"
              onClick={handleRefreshClick}
            >
              <i className="fa fa-refresh" aria-hidden="true" />
              &nbsp; Refresh campaign
            </button>
          </ElementTooltip>

          {!isHidden && (
            <ElementTooltip
              tooltipId="export-audit-file-tooltip"
              tooltip="Clicking this will allow you to download a CSV file including all rows from your Campaign Builder student list, all rows and columns from your uploaded file, and a column that informs you whether the student was in the file only, in your student list only, or in both the file and student list."
            >
              {/* GTM tracking via data-ga-* attributes.
                  Handled by centralized listener in @asu/component-header-footer.
                  Note: Using native button (not Unity Button) to preserve
                  styling when wrapped inside ElementTooltip. */}
              <button
                type="button"
                className="btn btn-maroon ms-3"
                aria-describedby="export-audit-file-tooltip"
                disabled={exportInProgress}
                data-ga-event="link"
                data-ga-action="click"
                data-ga-name="onclick"
                data-ga-type="button"
                data-ga-region="main content"
                data-ga-section="Campaign Details"
                data-ga-component=""
                data-ga-text="Download CSV file with campaign list, uploaded file, and match status."
                onClick={onExportAuditFile}
              >
                <i className="fas fa-file-export me-1" aria-hidden="true" />
                Export Audit File
              </button>
            </ElementTooltip>
          )}
        </div>
        {/* <div>
          <ElementTooltip
            tooltipId="publish-campaign-tooltip"
            tooltip={
              'This allows the Campaign to be available for use integrating with apps, ' +
              'sharing with other users or groups, etc. Once a Campaign is Published, ' +
              'you can no longer make edits to it.'
            }
          >
            <button
              className="btn btn-gold"
              type="button"
              disabled={campaign.published}
              aria-describedby="publish-campaign-tooltip"
              onClick={onPublishCampaign}
            >
              Publish Campaign
            </button>
          </ElementTooltip>
        </div> */}{' '}
        {/* Modal with Export options. */}
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            title="Export List"
            maxWidth="520px"
            fullHeightOnSmallScreens
            fillHeightOnSmallScreens={showNestedCheckbox}
            scrollBody
            onClose={toggleModal}
          >
            {' '}
            {showNestedCheckbox ? (
              <NestedCheckbox
                campaign={campaign}
                data={updatedInitialList}
                toggleModal={toggleModal}
                onCancel={onCancel}
                onExportList={handleFieldSelection}
              />
            ) : showSalesforceCredentials ? (
              <SalesforceCredentialsModal
                campaignId={campaign.campaignId}
                selectedFields={selectedFields}
                onCancel={onCancel}
              />
            ) : (
              <ExportTypeModal
                onCancel={onCancel}
                onExportTypeSelection={handleExportTypeSelection}
              />
            )}
          </Modal>
        )}
        {isSalesforceEnabled && isHistoryOpen && (
          <Modal
            isOpen={isHistoryOpen}
            title="Channel export history"
            maxWidth="1000px"
            showTitle={false}
            padding="24px 40px 24px 40px"
            scrollable
            fullHeightOnSmallScreens
            onClose={closeHistory}
          >
            <ChannelExportHistoryModal
              campaign={campaign}
              onClose={closeHistory}
            />
          </Modal>
        )}
      </div>
    </div>
  );
}

export { DetailList };
